/**
 * The questions an author pins to a learning moment as practice exemplars.
 *
 * Whoever writes a `detectionHint` almost always has two or three questions in
 * front of them that motivated it; those are the best practice questions that
 * exist for the moment, and until now they were thrown away. They are also the
 * answer to cold start: a moment authored this morning has no detection history
 * for the derived pool to fold, and would otherwise be unpractisable for weeks.
 *
 * Search, click to pin, ✕ to drop — the same chip shape as the module picker,
 * because a question id in a text box tells an author nothing.
 */

import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { adminApi } from '../../api/adminApi';
import type { QuestionListItem } from '../../types';

interface Props {
  value: string[];
  onChange: (ids: string[]) => void;
}

const preview = (text: string, max = 90) =>
  text.length > max ? `${text.slice(0, max)}…` : text;

export function ExemplarQuestionPicker({ value, onChange }: Props) {
  const [term, setTerm] = useState('');
  const [submitted, setSubmitted] = useState('');

  // Search only on submit: the question list is a paged server query, not
  // something to fire on every keystroke.
  const { data: results, isFetching } = useQuery({
    queryKey: ['lm-exemplar-search', submitted],
    queryFn: () => adminApi.getQuestionList({ searchString: submitted, pageSize: 10 }),
    enabled: submitted.trim().length > 2,
  });

  // Resolve the pinned ids so a chip reads as a question rather than an id.
  const { data: pinned } = useQuery({
    queryKey: ['lm-exemplar-pinned', value.join(',')],
    queryFn: () => Promise.all(value.map(id => adminApi.getQuestionById(id).catch(() => null))),
    enabled: value.length > 0,
  });
  const textById = new Map((pinned ?? []).filter(Boolean).map(q => [q!.id, q!.questionText ?? '']));

  const add = (id: string) => { if (!value.includes(id)) onChange([...value, id]); };

  return (
    <div className="lm-exemplars">
      <div className="lm-module-chips">
        {value.length === 0
          ? <span className="lm-module-empty">
              None pinned — a new moment has no detection history to draw practice from until students meet it.
            </span>
          : value.map(id => (
            <span key={id} className="lm-module-chip">
              {textById.has(id) ? preview(textById.get(id)!, 60) : id}
              <button type="button" onClick={() => onChange(value.filter(x => x !== id))} aria-label="Remove question">✕</button>
            </span>
          ))}
      </div>

      <div className="lm-exemplar-search">
        <input
          type="text"
          value={term}
          onChange={e => setTerm(e.target.value)}
          onKeyDown={e => { if (e.key === 'Enter') { e.preventDefault(); setSubmitted(term); } }}
          placeholder="Search question text, then Enter…"
        />
        <button type="button" className="btn-sm" onClick={() => setSubmitted(term)}>Search</button>
      </div>

      {isFetching && <p className="lm-hint">Searching…</p>}
      {results && results.questions.length === 0 && <p className="lm-hint">No questions matched.</p>}
      {results && results.questions.length > 0 && (
        <ul className="lm-exemplar-results">
          {results.questions.map((q: QuestionListItem) => (
            <li key={q.id}>
              <button type="button" onClick={() => add(q.id)} disabled={value.includes(q.id)}>
                {value.includes(q.id) ? '✓ ' : '+ '}{preview(q.questionText ?? '')}
              </button>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}
